import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { addExpense, fetchExpenseCategories } from "../services/api";
import { X } from "lucide-react";

export default function AddExpenseModal({ onClose, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [idExpenseCategory, setIdExpenseCategory] = useState("");
  const [description, setDescription] = useState("");
  const [categories, setCategories] = useState([]);
  const modalRef = useRef();

  useEffect(() => {
    fetchExpenseCategories().then(setCategories).catch(console.error);
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addExpense({
      expense_amt: Number(amount),
      id_expense_category: Number(idExpenseCategory),
      expense_description: description,
    });
    onSuccess();
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50 p-4"
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1] // Custom easing for smooth motion
          }}
          className="bg-gray-900 text-white p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-md relative"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-red-400 font-[Poppins]">
            Add Expense
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-300 mb-2 text-sm font-medium">
                Amount
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full p-3 border border-gray-700 rounded-lg bg-gray-800 text-white font-[Poppins] 
                           focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent
                           transition-all duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 text-sm font-medium">
                Category
              </label>
              <select
                value={idExpenseCategory}
                onChange={(e) => setIdExpenseCategory(e.target.value)}
                className="w-full p-3 border border-gray-700 rounded-lg bg-gray-800 text-white font-[Poppins] 
                           focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent
                           transition-all duration-200"
                required
              >
                <option value="">Select Category</option>
                {Array.isArray(categories) &&
                  categories.map((c) => (
                    <option
                      key={c.id_expense_category}
                      value={c.id_expense_category}
                    >
                      {c.expense_category_name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2 text-sm font-medium">
                Description
              </label>
              <input
                type="text"
                placeholder="Optional description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-3 border border-gray-700 rounded-lg bg-gray-800 text-white font-[Poppins] 
                           focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent
                           transition-all duration-200"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg border border-gray-600 text-gray-300 
                           hover:bg-gray-800 hover:border-gray-500 transition-all duration-200 font-[Poppins]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 py-3 rounded-lg bg-gradient-to-r from-red-500 to-rose-600 
                           hover:from-red-600 hover:to-rose-700 hover:shadow-lg hover:shadow-red-500/30
                           transition-all duration-200 font-[Poppins] font-semibold"
              >
                Add Expense
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}